import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { gatewayFetch, type PaddleEnv } from "@/lib/paddle.server";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { findAuthUserByEmail } from "@/lib/find-auth-user.server";
import type { Database } from "@/integrations/supabase/types";
import { cached } from "@/lib/server-ttl-cache";
import { pickActivePriceId } from "@/lib/paddle-prices";

import { siteOrigin } from "@/lib/site-origin.server";
import {
  PAYPAL_INSTRUCTIONS_REGION,
  purchasePaymentMethodForCountry,
} from "@/lib/payments/outside-syria";
import {
  resolveBousalaProduct,
  bousalaProductBySlug,
  type BousalaProductKey,
} from "@/lib/bousala/products";

/** Arabic product names shown in admin lists and buyer-facing copy. */
export const BOUSALA_PRODUCT_TITLES_AR: Record<BousalaProductKey, string> = {
  v1: "بوصلة د. سارة",
  smart: "البوصلة الذكية",
};

const productKeySchema = z.enum(["v1", "smart"]).optional();

function productFrom(key?: BousalaProductKey) {
  return resolveBousalaProduct(key);
}
// (outside-Syria uses Paddle — no payment_instructions row needed)
const RECEIPT_BUCKET = "payment-receipts";

/** True when the catalog row for this Bousala product is flagged free. */
async function isProductFree(admin: any, slug: string): Promise<boolean> {
  const { data } = await admin
    .from("products")
    .select("is_free")
    .eq("slug", slug)
    .maybeSingle();
  return data?.is_free === true;
}

// The purchasability guard now lives in @/lib/bousala/smart-preview
// (`assertPurchasableForViewer`) so it can also answer for an admin preview.

/**
 * Look up the buyer's preferred UI language from their profile. Falls back to
 * Arabic when the profile is missing or the value is unrecognised. Used to
 * localise WhatsApp/notification templates sent by admin actions.
 */
async function getPreferredLanguage(
  admin: any,
  userId: string | null,
): Promise<"ar" | "en"> {
  if (!userId) return "ar";
  try {
    const { data } = await admin
      .from("profiles")
      .select("preferred_language")
      .eq("user_id", userId)
      .maybeSingle();
    return data?.preferred_language === "en" ? "en" : "ar";
  } catch {
    return "ar";
  }
}

// Ensure the signed-in user has a granted purchase row for the Bousala product
// under free mode. Idempotent: returns existing grant when present.
// Concurrent callers (route beforeLoad + first-write ensureAttemptId) are
// coalesced in-process, and a post-insert reconciliation deletes any duplicate
// row this call created if another request won the race.
const freeGrantInFlight = new Map<string, Promise<{ purchaseId: string | null }>>();

function ensureFreeGrant(
  admin: any,
  userId: string,
  productKey?: BousalaProductKey,
): Promise<{ purchaseId: string | null }> {
  const key = productFrom(productKey).key;
  const cacheKey = `${key}:${userId}`;
  const existing = freeGrantInFlight.get(cacheKey);
  if (existing) return existing;
  const run = ensureFreeGrantUncoalesced(admin, userId, key).finally(() => {
    freeGrantInFlight.delete(cacheKey);
  });
  freeGrantInFlight.set(cacheKey, run);
  return run;
}

async function ensureFreeGrantUncoalesced(
  admin: any,
  userId: string,
  productKey?: BousalaProductKey,
): Promise<{ purchaseId: string | null }> {
  const def = productFrom(productKey);
  // Ensure profile
  let { data: profile } = await admin
    .from("profiles").select("id").eq("user_id", userId).maybeSingle();
  if (!profile) {
    const { data: created, error: pErr } = await admin
      .from("profiles").insert({ user_id: userId }).select("id").single();
    if (pErr) throw pErr;
    profile = created;
  }
  const { data: product } = await admin
    .from("products").select("id, is_free").eq("slug", def.slug).maybeSingle();
  if (!product) return { purchaseId: null };
  // Safety guard: never auto-grant a paid product, whatever the caller does.
  if (product.is_free !== true) return { purchaseId: null };

  const findGrant = async (): Promise<string | null> => {
    const { data: rows } = await admin
      .from("purchases")
      .select("id, status, created_at")
      .eq("client_id", profile.id)
      .eq("product_id", product.id)
      .in("status", ["paid", "granted"])
      .order("created_at", { ascending: true })
      .limit(1);
    return rows?.[0]?.id ?? null;
  };

  const found = await findGrant();
  if (found) return { purchaseId: found };

  const env = paddleEnvFromServer();
  const { data: row, error } = await admin
    .from("purchases")
    .insert({
      client_id: profile.id,
      product_id: product.id,
      status: "granted",
      payment_method: "free_mode",
      amount_cents: 0,
      currency: "USD",
      environment: env,
      granted_at: new Date().toISOString(),
    })
    .select("id")
    .single();
  if (error) {
    // The unique index purchases_one_free_grant covers (client_id, product_id)
    // for every free_mode row regardless of status, while findGrant only looks
    // at paid/granted. A pre-existing free_mode row in another status (pending,
    // rejected, refunded) therefore collides here. Recover by reusing that row.
    if ((error as { code?: string }).code === "23505") {
      const { data: existingRows } = await admin
        .from("purchases")
        .select("id, status, created_at")
        .eq("client_id", profile.id)
        .eq("product_id", product.id)
        .eq("payment_method", "free_mode")
        .order("created_at", { ascending: true })
        .limit(1);
      const existing = existingRows?.[0];
      if (existing) {
        if (existing.status !== "granted" && existing.status !== "paid") {
          await admin
            .from("purchases")
            .update({ status: "granted", granted_at: new Date().toISOString() })
            .eq("id", existing.id);
        }
        return { purchaseId: existing.id as string };
      }
    }
    throw error;
  }

  // Reconcile a lost race: if another request inserted a grant first, drop the
  // duplicate we just created and reuse the oldest one.
  const { data: all } = await admin
    .from("purchases")
    .select("id, created_at")
    .eq("client_id", profile.id)
    .eq("product_id", product.id)
    .in("status", ["paid", "granted"])
    .order("created_at", { ascending: true });
  const winner = all?.[0]?.id ?? (row.id as string);
  if (winner !== row.id) {
    await admin
      .from("purchases")
      .delete()
      .eq("id", row.id)
      .eq("payment_method", "free_mode");
    return { purchaseId: winner as string };
  }

  return { purchaseId: row.id as string };
}

// ---------- Public: free-mode indicator ----------

export const getBousalaMode = createServerFn({ method: "GET" })
  .validator((d: unknown) => z.object({ product: productKeySchema }).parse(d ?? {}))
  .handler(async ({ data }): Promise<{ free: boolean }> => {
    const def = productFrom(data.product);
    // Catalog flag, identical for every visitor — memoized for 30s so a
    // traffic spike does not turn into one query per pageview. The
    // authoritative check inside startBousalaFree stays uncached.
    return cached(`bousala:free_mode:${def.key}`, 30_000, async () => {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      return { free: await isProductFree(supabaseAdmin, def.slug) };
    });
  });

// ---------- Auth: grant free access on demand ----------

export const startBousalaFree = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({ lang: z.enum(["ar", "en"]).optional(), product: productKeySchema }).parse(d ?? {}),
  )
  .handler(async ({ context, data }): Promise<{ purchaseId: string | null }> => {
    const { userId } = context as { userId: string };
    const def = productFrom(data.product);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (!(await isProductFree(supabaseAdmin, def.slug))) {
      throw new Error(
        data.lang === "en"
          ? "Free mode is not enabled right now."
          : "الوضع المجاني غير مفعّل حاليًا.",
      );
    }
    return ensureFreeGrant(supabaseAdmin, userId, def.key);
  });

function serverPublic() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}


// ---------- Public: buy page bootstrap ----------

export const getBousalaBuyContext = createServerFn({ method: "GET" })
  .validator((d: unknown) => z.object({ product: productKeySchema }).parse(d ?? {}))
  .handler(async ({ data }) => {
  const def = productFrom(data.product);
  // Public catalog rows — same for everyone, memoized for 60s.
  return cached(`bousala:buy_context:${def.key}`, 60_000, async () => {
    const sb = serverPublic();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: product }, { data: instructions }, { data: paypal }, { data: catalog }] =
      await Promise.all([
        sb
          .from("products")
          .select("id, slug, title_ar, description_ar, price_usd, is_active, is_free")
          .eq("slug", def.slug)
          .eq("is_active", true)
          .maybeSingle(),
        sb
          .from("payment_instructions")
          .select("region_slug, title_ar, body_md")
          .eq("region_slug", def.instructionsRegion)
          .eq("is_active", true)
          .maybeSingle(),
        sb
          .from("payment_instructions")
          .select("region_slug, title_ar, body_md")
          .eq("region_slug", PAYPAL_INSTRUCTIONS_REGION)
          .eq("is_active", true)
          .maybeSingle(),
        // Display only: the price/title of a product that is not launched yet.
        // No purchase path reads this — the guards keep using `product`.
        supabaseAdmin
          .from("products")
          .select("slug, title_ar, description_ar, price_usd, is_active, is_free")
          .eq("slug", def.slug)
          .maybeSingle(),
      ]);
    return {
      product: product ?? null,
      catalog: catalog ?? null,
      inside_syria: instructions ?? null,
      paypal_outside_syria: paypal ?? null,
    };
  });
});

// ---------- Public: Paddle price resolution (unauth variant) ----------

// Paddle was removed from the product (account rejected 18 Sep 2026). The
// endpoint stays so old clients get a clear refusal instead of a checkout.
export const resolveBousalaPaddlePrice = createServerFn({ method: "GET" })
  .validator((d: unknown) =>
    z.object({ environment: z.enum(["sandbox", "live"]), product: productKeySchema }).parse(d),
  )
  .handler(async () => {
    throw new Error("غير متاح");
  });

// ---------- Public: signed upload URL for local receipt ----------

function safeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-80);
}

export const getBousalaReceiptUploadUrl = createServerFn({ method: "POST" })
  .validator((d: unknown) =>
    z.object({ filename: z.string().min(1).max(160) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const path = `bousala/pending/${crypto.randomUUID()}-${safeFilename(data.filename)}`;
    const { data: signed, error } = await supabaseAdmin.storage
      .from(RECEIPT_BUCKET)
      .createSignedUploadUrl(path);
    if (error) throw error;
    return { path: signed.path, token: signed.token };
  });

// ---------- Public: create local-transfer purchase (inside Syria) ----------

const localInput = z.object({
  email: z.string().email().max(200),
  full_name: z.string().min(2).max(120),
  phone: z.string().min(6).max(40),
  receipt_path: z.string().min(1).max(400),
  lang: z.enum(["ar", "en"]).optional(),
  product: productKeySchema,
  /** ISO country of the buyer; anything other than "SY" is the PayPal rail. */
  country: z.string().min(2).max(2).optional(),
});
type LocalPurchaseInput = z.infer<typeof localInput>;

function paddleEnvFromServer(): PaddleEnv {
  const token = process.env.VITE_PAYMENTS_CLIENT_TOKEN;
  return token?.startsWith("test_") ? "sandbox" : "live";
}

async function submitBousalaLocalPurchaseCore(
  supabaseAdmin: any,
  data: LocalPurchaseInput,
  viewerIsAdmin: boolean,
) {
    const def = productFrom(data.product);
    const email = data.email.trim().toLowerCase();
    // Guard: an unlaunched product must never be purchasable — except for a
    // signed-in admin rehearsing the flow, verified here on the server. Those
    // purchases are tagged environment = 'sandbox'.
    const { assertPurchasableForViewer } = await import("@/lib/bousala/smart-preview");
    const { preview: adminPreview } = await assertPurchasableForViewer(
      supabaseAdmin,
      def.slug,
      viewerIsAdmin,
      data.lang,
    );
    // Look up product first
    const { data: product, error: prodErr } = await supabaseAdmin
      .from("products")
      .select("id, price_usd")
      .eq("slug", def.slug)
      .single();
    if (prodErr) throw prodErr;

    // If this email already has an active (granted/paid) Bousala purchase, short-circuit.
    const matchUser = await findAuthUserByEmail(supabaseAdmin, email);
    if (matchUser) {
      const { data: prof } = await supabaseAdmin
        .from("profiles").select("id").eq("user_id", matchUser.id).maybeSingle();
      if (prof) {
        const { data: existingActive } = await supabaseAdmin
          .from("purchases")
          .select("id, status")
          .eq("client_id", prof.id)
          .eq("product_id", product.id)
          .in("status", ["paid", "granted"])
          .maybeSingle();
        if (existingActive) {
          return { pendingId: null as string | null, alreadyGranted: true };
        }
      }
    }

    const env = adminPreview ? "sandbox" : paddleEnvFromServer();

    // Insert into pending_purchases (no auth user created yet).
    // Unique partial index enforces one pending row per (email, product).
    const { data: pending, error: pErr } = await supabaseAdmin
      .from("pending_purchases")
      .insert({
        product_id: product.id,
        email,
        full_name: data.full_name,
        phone: data.phone,
        country: data.country ?? "SY",
        receipt_file: data.receipt_path,
        amount_cents: Math.round(Number(product.price_usd) * 100),
        currency: "USD",
        environment: env,
        status: "pending",
      })
      .select("id")
      .single();
    if (pErr) {
      // Duplicate pending submission
      if (pErr.code === "23505") {
        throw new Error(
          data.lang === "en"
            ? "You already have a request under review. We'll contact you shortly."
            : "لديك طلب قيد المراجعة بالفعل. سنتواصل معك قريبًا.",
        );
      }
      throw pErr;
    }

    return { pendingId: pending.id as string, alreadyGranted: false };
}

export async function updateOwnPreferredLanguageCore(
  user: any,
  userId: string,
  lang: "ar" | "en",
): Promise<void> {
  const { error } = await user
    .from("profiles")
    .update({ preferred_language: lang })
    .eq("user_id", userId);
  if (error) throw error;
}

export const submitBousalaLocalPurchase = createServerFn({ method: "POST" })
  .validator((d: unknown) => localInput.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { requestIsAdmin } = await import("@/lib/bousala/smart-preview.server");
    return submitBousalaLocalPurchaseCore(supabaseAdmin, data, await requestIsAdmin());
  });

/**
 * The buyer's real account email. The access token can still be the
 * pre-email anonymous one right after the add-email step, so the account
 * record — not the token claim — is the source of truth. The email the
 * browser sends is never trusted for identity.
 */
export async function resolveAccountEmailCore(
  admin: any,
  userId: string,
  claimsEmail?: unknown,
): Promise<string> {
  const { data, error } = await admin.auth.admin.getUserById(userId);
  if (error) throw error;
  const accountEmail = String(data?.user?.email ?? "").trim().toLowerCase();
  if (accountEmail) return accountEmail;
  return String(claimsEmail ?? "").trim().toLowerCase();
}

export const submitSmartReceiptPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => localInput.omit({ product: true }).parse(d))
  .handler(async ({ context, data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { requestIsAdmin } = await import("@/lib/bousala/smart-preview.server");
    const accountEmail = await resolveAccountEmailCore(
      supabaseAdmin,
      context.userId,
      (context.claims as { email?: unknown })?.email,
    );
    if (!accountEmail) {
      throw new Error(
        data.lang === "en"
          ? "Please confirm your email first, then send the receipt."
          : "تأكيد البريد الإلكتروني أولًا، وبعده إرسال الإيصال.",
      );
    }
    await updateOwnPreferredLanguageCore(context.supabase, context.userId, data.lang ?? "ar");
    return submitBousalaLocalPurchaseCore(
      supabaseAdmin,
      { ...data, email: accountEmail, product: "smart" },
      await requestIsAdmin(),
    );
  });


// The public, email-keyed status poll and the public magic-link resend that
// served the retired Paddle success page were removed: both let anyone probe
// whether an email address has an account here. Signed-in access goes through
// getBousalaAccess; buyers are contacted on WhatsApp after approval.

/** Where a buyer of this product should land after signing in. */
export function bousalaEntryPath(key: BousalaProductKey): string {
  return key === "smart" ? "/portal/smart" : "/bousala/play";
}

/** Maps a products.id to a Bousala product key; unknown rows fall back to v1. */
async function productKeyForProductId(
  admin: any,
  productId: string | null | undefined,
): Promise<BousalaProductKey> {
  if (!productId) return "v1";
  const { data } = await admin
    .from("products").select("slug").eq("id", productId).maybeSingle();
  return bousalaProductBySlug(data?.slug ?? "")?.key ?? "v1";
}

/** Has the buyer's Smart order already been bound to a scored attempt? */
async function smartAssessmentDone(admin: any, purchaseId: string | null): Promise<boolean> {
  if (!purchaseId) return false;
  const { data } = await admin
    .from("smart_reports")
    .select("status")
    .eq("purchase_id", purchaseId)
    .maybeSingle();
  return data ? data.status !== "paid" && data.status !== "payment_pending_review" : false;
}

/** Resend WhatsApp copy, mirroring the approval message. */
function smartResendMessage(
  key: BousalaProductKey,
  lang: "ar" | "en",
  fullName: string,
  magicLink: string,
  assessmentDone: boolean,
): string {
  if (key !== "smart") {
    return lang === "en"
      ? `Hi ${fullName}\n` +
        `Here's your sign-in link for "Dr. Sara's Compass":\n${magicLink}\n\n` +
        `The link is valid for a limited time — if it expires, message us to resend.`
      : `مرحبًا ${fullName}\n` +
        `هذا رابط الدخول إلى «بوصلة د. سارة»:\n${magicLink}\n\n` +
        `الرابط صالح لمدة محدودة — إذا انتهت صلاحيته، راسلونا لإعادة الإرسال.`;
  }
  if (!assessmentDone) {
    return lang === "en"
      ? `Hi ${fullName}\n` +
        `Here's your sign-in link for "Smart Bousala" — finish the free Bousala test from it first:\n${magicLink}\n\n` +
        `The preferences form opens right after, and your report arrives within 24 hours of submitting it.`
      : `مرحبًا ${fullName}\n` +
        `هذا رابط الدخول إلى «البوصلة الذكية» — أول خطوة منه اختبار البوصلة:\n${magicLink}\n\n` +
        `بعدها مباشرة بينفتح نموذج التفضيلات، ومنوصلك التقرير خلال 24 ساعة من تعبئته.`;
  }
  return lang === "en"
    ? `Hi ${fullName}\n` +
      `Here's your sign-in link for "Smart Bousala" — fill in the preferences form from it:\n${magicLink}\n\n` +
      `Your report is delivered within 24 hours of submitting the form.`
    : `مرحبًا ${fullName}\n` +
      `هذا رابط الدخول إلى «البوصلة الذكية» — منه تعبئة نموذج التفضيلات:\n${magicLink}\n\n` +
      `منوصلك التقرير خلال 24 ساعة من تعبئة النموذج.`;
}

/** Approval / resend WhatsApp copy. Smart buyers are pointed at the
 *  preferences form; the Bousala 1 wording is untouched. */
function smartApprovalMessage(
  key: BousalaProductKey,
  lang: "ar" | "en",
  fullName: string,
  magicLink: string,
  /** true once the order reached `assessment_complete` (the free test is done). */
  assessmentDone = true,
): string {
  if (key === "smart") {
    if (!assessmentDone) {
      return lang === "en"
        ? `Hi ${fullName}\n` +
          `Your payment for "Smart Bousala" is confirmed. Next step: finish the free Bousala test from this link:\n${magicLink}\n\n` +
          `Right after you finish it, the preferences form opens for you — and your report arrives within 24 hours of submitting it.`
        : `مرحبًا ${fullName}\n` +
          `تم تأكيد دفعتك لـ«البوصلة الذكية». الخطوة التالية: اختبار البوصلة المجاني من هذا الرابط:\n${magicLink}\n\n` +
          `أول ما تخلّصه بينفتح لك نموذج التفضيلات، ومنوصلك التقرير خلال 24 ساعة من تعبئته.`;
    }
    return lang === "en"
      ? `Hi ${fullName}\n` +
        `Your payment for "Smart Bousala" is confirmed. Next step: fill in the preferences form from this link:\n${magicLink}\n\n` +
        `Your report is delivered within 24 hours of submitting the form, after Dr. Sara's team reviews it.`
      : `مرحبًا ${fullName}\n` +
        `تم تأكيد دفعتك لـ«البوصلة الذكية». الخطوة التالية: تعبئة نموذج التفضيلات من هذا الرابط:\n${magicLink}\n\n` +
        `منوصلك التقرير خلال 24 ساعة من تعبئة النموذج، بعد مراجعة فريق د. سارة له.`;
  }
  return lang === "en"
    ? `Hi ${fullName}\n` +
      `Your account for "Dr. Sara's Compass" is now active. You can start the test at this link:\n${magicLink}\n\n` +
      `The link is valid for a limited time — if it expires, message us to resend.`
    : `مرحبًا ${fullName}\n` +
      `تم تفعيل حسابك في «بوصلة د. سارة». البدء بالاختبار صار متاح عبر الرابط التالي:\n${magicLink}\n\n` +
      `الرابط صالح لمدة محدودة — إذا انتهت صلاحيته، راسلونا لإعادة الإرسال.`;
}

// ---------- Auth: gate for /bousala/play ----------

export type BousalaAccessStatus = "granted" | "pending" | "rejected" | "none";

export const getBousalaAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ product: productKeySchema }).parse(d ?? {}))
  .handler(async ({ context, data }): Promise<{ status: BousalaAccessStatus; purchaseId: string | null }> => {
    const { supabase, userId } = context as { supabase: any; userId: string };
    const def = productFrom(data.product);
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      if (await isProductFree(supabaseAdmin, def.slug)) {
        const { purchaseId } = await ensureFreeGrant(supabaseAdmin, userId, def.key);
        return { status: "granted", purchaseId };
      }
      const { data: profile } = await supabase.from("profiles").select("id").eq("user_id", userId).maybeSingle();
      if (!profile) return { status: "none", purchaseId: null };
      const { data: product } = await supabase.from("products").select("id").eq("slug", def.slug).maybeSingle();
      if (!product) return { status: "none", purchaseId: null };

      const { data: purchase } = await supabase
        .from("purchases")
        .select("id, status")
        .eq("client_id", profile.id)
        .eq("product_id", product.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!purchase) return { status: "none", purchaseId: null };
      const status = purchase.status as string;
      if (status === "paid" || status === "granted") return { status: "granted", purchaseId: purchase.id };
      if (status === "pending") return { status: "pending", purchaseId: purchase.id };
      if (status === "rejected") return { status: "rejected", purchaseId: purchase.id };
      return { status: "none", purchaseId: purchase.id };
    } catch (err: any) {
      console.error("[bousala] getBousalaAccess failed", {
        userId,
        message: err?.message ?? String(err),
        code: err?.code,
        details: err?.details,
        hint: err?.hint,
      });
      throw err;
    }
  });

// Returns access + in-progress or completed attempt info for the signed-in user.
// Used by the portal, site header, landing CTA, and post-login redirect to surface
// "resume test" affordances without hunting.
export type BousalaResumeState = {
  access: BousalaAccessStatus;
  attemptId: string | null;
  attemptStatus:
    | "in_progress"
    | "completed"
    | "submitted"
    | "scored"
    | "report_ready"
    | null;
  answersCount: number;
};

export const getBousalaResumeState = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ product: productKeySchema }).parse(d ?? {}))
  .handler(async ({ context, data }): Promise<BousalaResumeState> => {
    const { supabase, userId } = context as { supabase: any; userId: string };
    const def = productFrom(data.product);
    try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const freeMode = await isProductFree(supabaseAdmin, def.slug);
    if (freeMode) {
      await ensureFreeGrant(supabaseAdmin, userId, def.key);
    }
    const { data: profile } = await supabase
      .from("profiles").select("id").eq("user_id", userId).maybeSingle();
    if (!profile) return { access: "none", attemptId: null, attemptStatus: null, answersCount: 0 };

    const { data: product } = await supabase
      .from("products").select("id").eq("slug", def.slug).maybeSingle();

    // Determine access
    let access: BousalaAccessStatus = "none";
    if (product) {
      const { data: purchase } = await supabase
        .from("purchases")
        .select("id, status")
        .eq("client_id", profile.id)
        .eq("product_id", product.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (purchase) {
        const s = purchase.status as string;
        access = s === "paid" || s === "granted"
          ? "granted"
          : s === "pending"
          ? "pending"
          : s === "rejected"
          ? "rejected"
          : "none";
      }
    }

    // Look up the most recent attempt (in-progress preferred, else latest completed).
    const { data: instrument } = await supabaseAdmin
      .from("instruments").select("id").eq("slug", def.instrumentSlug).maybeSingle();
    if (!instrument) {
      return { access, attemptId: null, attemptStatus: null, answersCount: 0 };
    }

    const { data: inProgress } = await supabase
      .from("attempts")
      .select("id, status, answers")
      .eq("client_id", profile.id)
      .eq("instrument_id", instrument.id)
      .eq("status", "in_progress")
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (inProgress) {
      const answersCount = Object.keys((inProgress.answers ?? {}) as Record<string, unknown>).length;
      return {
        access,
        attemptId: inProgress.id as string,
        attemptStatus: "in_progress",
        answersCount,
      };
    }

    const { data: latest } = await supabase
      .from("attempts")
      .select("id, status")
      .eq("client_id", profile.id)
      .eq("instrument_id", instrument.id)
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!latest) return { access, attemptId: null, attemptStatus: null, answersCount: 0 };
    return {
      access,
      attemptId: latest.id as string,
      attemptStatus: latest.status as BousalaResumeState["attemptStatus"],
      answersCount: 0,
    };
    } catch (err: any) {
      console.error("[bousala] getBousalaResumeState failed", {
        userId,
        message: err?.message ?? String(err),
        code: err?.code,
        details: err?.details,
        hint: err?.hint,
      });
      throw err;
    }
  });

// ---------- Admin: list & moderate local purchases ----------

async function assertAdmin(supabase: any, userId: string) {
  const { data: rolesRows } = await supabase
    .from("user_roles").select("role").eq("user_id", userId);
  const roles = (rolesRows ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden");
}

export const listBousalaPurchasesAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ product: productKeySchema }).parse(d ?? {}))
  .handler(async ({ context, data: input }) => {
    await assertAdmin(context.supabase, context.userId);
    const def = productFrom(input.product);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: product } = await supabaseAdmin
      .from("products").select("id").eq("slug", def.slug).single();
    if (!product) return [];
    // Pending (Syria receipt, not yet approved) + resolved (paddle-paid or approved locally).
    const { data: pendings } = await supabaseAdmin
      .from("pending_purchases")
      .select("id, status, receipt_file, amount_cents, currency, environment, created_at, reason, email, full_name, phone, country")
      .eq("product_id", product.id)
      .order("created_at", { ascending: false })
      .limit(200);
    const { data, error } = await supabaseAdmin
      .from("purchases")
      .select("id, status, payment_method, receipt_file, amount_cents, currency, environment, paddle_transaction_id, created_at, granted_at, client_id")
      .eq("product_id", product.id)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    const rows = data ?? [];
    // build user/profile map for resolved purchases
    const clientIds = Array.from(new Set(rows.map((r) => r.client_id).filter(Boolean))) as string[];
    const { data: profiles } = clientIds.length ? await supabaseAdmin
      .from("profiles")
      .select("id, user_id, full_name, phone_whatsapp, country")
      .in("id", clientIds) : { data: [] as any[] };
    const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

    // Look up emails via auth admin
    const userIds = Array.from(new Set((profiles ?? []).map((p) => p.user_id).filter(Boolean))) as string[];
    const emailMap = new Map<string, string>();
    if (userIds.length > 0) {
      const results = await Promise.all(
        userIds.map((id) => supabaseAdmin.auth.admin.getUserById(id)),
      );
      for (let i = 0; i < userIds.length; i++) {
        const u = results[i]?.data?.user;
        if (u) emailMap.set(userIds[i], u.email ?? "");
      }
    }

    const pendingRows = (pendings ?? [])
      .filter((r) => r.status === "pending" || r.status === "rejected")
      .map((r) => ({
        kind: "pending" as const,
        id: r.id,
        status: r.status, // 'pending' | 'rejected'
        payment_method: purchasePaymentMethodForCountry(r.country),
        receipt_file: r.receipt_file,
        amount_cents: r.amount_cents,
        currency: r.currency,
        environment: r.environment,
        paddle_transaction_id: null,
        created_at: r.created_at,
        granted_at: null,
        reason: r.reason,
        profile: {
          full_name: r.full_name ?? "",
          phone_whatsapp: r.phone ?? "",
          country: r.country ?? "",
          email: r.email ?? "",
        },
      }));

    const resolvedRows = rows.map((r) => {
      const p = r.client_id ? profileMap.get(r.client_id) : null;
      return {
        kind: "purchase" as const,
        ...r,
        profile: p
          ? {
              full_name: p.full_name ?? "",
              phone_whatsapp: p.phone_whatsapp ?? "",
              country: p.country ?? "",
              email: emailMap.get(p.user_id ?? "") ?? "",
            }
          : null,
      };
    });

    // Interleave, pending first (newest), then paid/granted.
    const productLabel = {
      product_key: def.key,
      product_title: BOUSALA_PRODUCT_TITLES_AR[def.key],
    };
    return [...pendingRows, ...resolvedRows].map((r) => ({ ...r, ...productLabel }));
  });

export const getBousalaReceiptSignedUrl = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ path: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error } = await supabaseAdmin.storage
      .from(RECEIPT_BUCKET)
      .createSignedUrl(data.path, 60 * 10);
    if (error) throw error;
    return { url: signed.signedUrl };
  });

export const approveBousalaPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({
      pendingId: z.string().uuid().optional(),
      purchaseId: z.string().uuid().optional(),
    }).refine((v) => !!v.pendingId || !!v.purchaseId, "id required").parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { ensureSmartOrderForPurchase } = await import("@/lib/bousala/smart-order.server");
    let smartOrderCreated = false;



    let email = "";
    let phone = "";
    let fullName = "";
    let purchaseId = data.purchaseId ?? null;
    let userId: string | null = null;
    let pendingRow: any = null;
    let existingAccount = false;
    let productKey: BousalaProductKey = "v1";

    if (data.pendingId) {
      const { data: p, error: e } = await supabaseAdmin
        .from("pending_purchases")
        .select("id, status, product_id, email, full_name, phone, country, receipt_file, amount_cents, currency, environment")
        .eq("id", data.pendingId)
        .single();
      if (e) throw e;
      if (p.status !== "pending") throw new Error("هذا الطلب معالج مسبقًا.");
      pendingRow = p;
      productKey = await productKeyForProductId(supabaseAdmin, p.product_id);
      email = String(p.email).trim().toLowerCase();
      phone = p.phone ?? "";
      fullName = p.full_name ?? "";

      // Find or create the auth user (deferred until now). An existing
      // account keeps its contact details and gets no sign-in link; staff
      // accounts are refused so the admin handles them by hand.
      const acct = await import("@/lib/approval-account.server");
      const resolved = await acct.resolveApprovalAccount(supabaseAdmin, email, fullName);
      userId = resolved.userId;
      existingAccount = resolved.existing;
      const profileRow = {
        id: await acct.profileForApproval(supabaseAdmin, userId, resolved.existing, {
          full_name: fullName,
          phone_whatsapp: phone,
          country: p.country ?? "SY",
        }),
      };

      // Create the granted purchase row.
      const { data: purchase, error: purErr } = await supabaseAdmin
        .from("purchases")
        .insert({
          client_id: profileRow.id,
          product_id: p.product_id,
          status: "granted",
          payment_method: purchasePaymentMethodForCountry(p.country),
          receipt_file: p.receipt_file,
          amount_cents: p.amount_cents,
          currency: p.currency ?? "USD",
          tier: "full",
          environment: p.environment ?? "sandbox",
          granted_at: new Date().toISOString(),
        })
        .select("id")
        .single();
      if (purErr) throw purErr;
      purchaseId = purchase.id;

      // Smart Bousala only (guarded inside): open the order row. Never throw —
      // access is already granted and the admin must not see a hard error.
      try {
        const r = await ensureSmartOrderForPurchase(supabaseAdmin, purchase.id);
        smartOrderCreated = r.created;
      } catch (e: any) {
        console.error("[bousala-approve] smart order create failed", {
          purchaseId: purchase.id,
          error: e?.message,
        });
      }



      // Close the pending row.
      await supabaseAdmin
        .from("pending_purchases")
        .update({
          status: "approved",
          reviewed_by: context.userId,
          reviewed_at: new Date().toISOString(),
          approved_purchase_id: purchase.id,
          wa_notified_at: new Date().toISOString(),
        })
        .eq("id", data.pendingId);
    } else if (data.purchaseId) {
      // Legacy path — an existing purchases row.
      const { data: purchase, error: pErr } = await supabaseAdmin
        .from("purchases")
        .select("id, client_id, product_id")
        .eq("id", data.purchaseId)
        .single();
      if (pErr) throw pErr;
      productKey = await productKeyForProductId(supabaseAdmin, purchase.product_id);
      const { data: profile } = await supabaseAdmin
        .from("profiles")
        .select("user_id, phone_whatsapp, full_name")
        .eq("id", purchase.client_id)
        .maybeSingle();
      if (!profile?.user_id) throw new Error("Profile has no user");
      const { data: userData } = await supabaseAdmin.auth.admin.getUserById(profile.user_id);
      email = userData?.user?.email ?? "";
      phone = profile.phone_whatsapp ?? "";
      fullName = profile.full_name ?? "";
      userId = profile.user_id;
      if (!email) throw new Error("User has no email");
      await supabaseAdmin
        .from("purchases")
        .update({ status: "granted", granted_at: new Date().toISOString() })
        .eq("id", data.purchaseId);

      try {
        const r = await ensureSmartOrderForPurchase(supabaseAdmin, data.purchaseId);
        smartOrderCreated = r.created;
      } catch (e: any) {
        console.error("[bousala-approve] smart order create failed", {
          purchaseId: data.purchaseId,
          error: e?.message,
        });
      }
    }


    if (existingAccount) {
      const { existingAccountResult } = await import("@/lib/approval-account.server");
      const lang = await getPreferredLanguage(supabaseAdmin, userId);
      return { ...existingAccountResult(phone, fullName, email, lang), smartOrderCreated };
    }

    const origin = siteOrigin();
    const redirectTo = `${origin}${bousalaEntryPath(productKey)}`;
    const { data: linkData, error: linkErr } = await supabaseAdmin.auth.admin.generateLink({
      type: "magiclink",
      email,
      options: { redirectTo },
    });
    if (linkErr) throw linkErr;
    const magicLink = linkData?.properties?.action_link ?? "";

    const buyerLang = await getPreferredLanguage(supabaseAdmin, userId);
    const assessmentDone =
      productKey === "smart" ? await smartAssessmentDone(supabaseAdmin, purchaseId) : true;
    const message = smartApprovalMessage(
      productKey,
      buyerLang,
      fullName,
      magicLink,
      assessmentDone,
    );
    const waLink = toWhatsAppLink(phone, message);

    return { magicLink, waLink, smartOrderCreated };

  });

export const rejectBousalaPurchase = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({
      pendingId: z.string().uuid().optional(),
      purchaseId: z.string().uuid().optional(),
      reason: z.string().min(2).max(500),
    }).refine((v) => !!v.pendingId || !!v.purchaseId, "id required").parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let email = "";
    let phone = "";
    let fullName = "";
    if (data.pendingId) {
      const { data: p, error } = await supabaseAdmin
        .from("pending_purchases")
        .select("id, email, full_name, phone")
        .eq("id", data.pendingId)
        .single();
      if (error) throw error;
      email = String(p.email);
      phone = p.phone ?? "";
      fullName = p.full_name ?? "";
      await supabaseAdmin
        .from("pending_purchases")
        .update({
          status: "rejected",
          reason: data.reason,
          reviewed_by: context.userId,
          reviewed_at: new Date().toISOString(),
          wa_notified_at: new Date().toISOString(),
        })
        .eq("id", data.pendingId);
    } else if (data.purchaseId) {
      const { error } = await supabaseAdmin
        .from("purchases")
        .update({ status: "rejected" })
        .eq("id", data.purchaseId);
      if (error) throw error;
    }

    // Build a WhatsApp Web link with the rejection reason so admin can click-to-send.
    // Best-effort lookup of the buyer's preferred language via their auth user.
    let buyerLang: "ar" | "en" = "ar";
    if (email) {
      const match = await findAuthUserByEmail(supabaseAdmin, email);
      if (match?.id) buyerLang = await getPreferredLanguage(supabaseAdmin, match.id);
    }
    const message = buyerLang === "en"
      ? `Hi ${fullName || ""}\n` +
        `Thanks for reaching out to "Dr. Sara's Compass". Unfortunately we couldn't confirm your payment receipt for the following reason:\n` +
        `${data.reason}\n\n` +
        `We're happy to help — message us to try again.`
      : `مرحبًا ${fullName || ""}\n` +
        `شكرًا لتواصلك مع «بوصلة د. سارة». للأسف تعذّر تأكيد إيصال الدفع للسبب التالي:\n` +
        `${data.reason}\n\n` +
        `يسعدنا مساعدتك — راسلونا لإعادة المحاولة.`;
    const waLink = toWhatsAppLink(phone, message);

    return { ok: true, waLink };
  });

// Resend the activation link over WhatsApp for an already-approved purchase.
// Regenerates a fresh magic link — does not change purchase status.
export const resendBousalaWhatsApp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) =>
    z.object({ purchaseId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: purchase, error: pErr } = await supabaseAdmin
      .from("purchases")
      .select("id, client_id, status, product_id")
      .eq("id", data.purchaseId)
      .single();
    if (pErr) throw pErr;
    if (purchase.status !== "granted" && purchase.status !== "paid") {
      throw new Error("لا يمكن إعادة الإرسال إلا للطلبات المفعّلة.");
    }

    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("user_id, phone_whatsapp, full_name")
      .eq("id", purchase.client_id)
      .maybeSingle();
    if (!profile?.user_id) throw new Error("لا يوجد حساب مرتبط.");

    const { data: userData } = await supabaseAdmin.auth.admin.getUserById(profile.user_id);
    const email = userData?.user?.email ?? "";
    if (!email) throw new Error("لا يوجد بريد إلكتروني للمستخدمة.");
    const fullName = profile.full_name ?? "";
    const phone = profile.phone_whatsapp ?? "";

    const productKey = await productKeyForProductId(supabaseAdmin, purchase.product_id);
    const origin = siteOrigin();
    const redirectTo = `${origin}${bousalaEntryPath(productKey)}`;
    const { data: linkData, error: linkErr } = await supabaseAdmin.auth.admin.generateLink({
      type: "magiclink",
      email,
      options: { redirectTo },
    });
    if (linkErr) throw linkErr;
    const magicLink = linkData?.properties?.action_link ?? "";

    const buyerLang = await getPreferredLanguage(supabaseAdmin, profile.user_id);
    const resendAssessmentDone =
      productKey === "smart" ? await smartAssessmentDone(supabaseAdmin, purchase.id) : true;
    const message = smartResendMessage(
      productKey,
      buyerLang,
      fullName,
      magicLink,
      resendAssessmentDone,
    );
    const waLink = toWhatsAppLink(phone, message);

    return { magicLink, waLink };
  });